import {
  LayoutDashboard, Users, Pill, CalendarDays, ShieldCheck, FileText,
  ClipboardList, HeartPulse, AlertTriangle, GraduationCap, Wallet,
  UploadCloud, CheckCircle2, TrendingUp, Activity,
} from "lucide-react";

/* Polished browser/device frames wrapping CSS product mockups.
   These are placeholders styled to look like real AYOBELL screens and
   can be swapped for real screenshots later. */

/* BrowserFrame is the single wrapper for every product visual.
   To use a REAL AYOBELL screenshot, pass `image` (+ `alt`); the CSS mockup
   passed as `children` is only a temporary placeholder and is ignored when
   `image` is provided. This keeps swapping to real screenshots a one-line change. */
export function BrowserFrame({ children, image, alt = "AYOBELL product screenshot", url = "app.ayobell.com", className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white shadow-[0_30px_70px_-20px_rgba(11,25,44,0.35)] overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex gap-1.5">
          <span className="w-3 h-3 rounded-full bg-slate-300" />
          <span className="w-3 h-3 rounded-full bg-slate-300" />
          <span className="w-3 h-3 rounded-full bg-slate-300" />
        </div>
        <div className="mx-auto flex items-center gap-2 px-3 py-1 rounded-md bg-white border border-slate-200 text-[11px] text-slate-500">
          <ShieldCheck className="w-3 h-3 text-teal-600" />
          {url}
        </div>
      </div>
      <div className="bg-slate-50">
        {image ? <img src={image} alt={alt} className="block w-full h-auto" /> : children}
      </div>
    </div>
  );
}

const NavItem = ({ icon: Icon, label, active }) => (
  <div className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium ${active ? "bg-teal-50 text-teal-800" : "text-slate-500"}`}>
    <Icon className={`w-4 h-4 ${active ? "text-teal-600" : "text-slate-400"}`} />
    <span className="truncate">{label}</span>
  </div>
);

function Sidebar({ active = "Dashboard" }) {
  const items = [
    ["Dashboard", LayoutDashboard],
    ["Service Users", Users],
    ["Care Plans", ClipboardList],
    ["Medication", Pill],
    ["Rota & Visits", CalendarDays],
    ["Governance", ShieldCheck],
    ["Payroll", Wallet],
  ];
  return (
    <div className="hidden sm:flex w-40 shrink-0 flex-col gap-1 p-3 bg-white border-r border-slate-200">
      <div className="flex items-center gap-2 px-2 py-2 mb-2">
        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-teal-500 to-emerald-500" />
        <span className="font-display font-extrabold text-slate-900 text-sm tracking-tight">AYOBELL</span>
      </div>
      {items.map(([label, icon]) => (
        <NavItem key={label} icon={icon} label={label} active={label === active} />
      ))}
    </div>
  );
}

const Stat = ({ label, value, tone = "teal", trend }) => {
  const tones = {
    teal: "text-teal-700 bg-teal-50",
    emerald: "text-emerald-700 bg-emerald-50",
    amber: "text-amber-700 bg-amber-50",
    slate: "text-slate-700 bg-slate-100",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <div className="flex items-end justify-between mt-1">
        <p className="text-xl font-bold text-slate-900 font-display">{value}</p>
        {trend && (
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${tones[tone]}`}>{trend}</span>
        )}
      </div>
    </div>
  );
};

function MiniBars({ heights, color = "bg-teal-500" }) {
  return (
    <div className="flex items-end gap-1.5 h-20">
      {heights.map((h, i) => (
        <div key={i} className="flex-1 rounded-t-sm bg-slate-100 flex items-end overflow-hidden">
          <div className={`w-full ${color} rounded-t-sm`} style={{ height: `${h}%` }} />
        </div>
      ))}
    </div>
  );
}

/* ---- Screens ---- */

export function ManagerDashboard() {
  return (
    <div className="flex min-h-[320px] text-left">
      <Sidebar active="Dashboard" />
      <div className="flex-1 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] text-slate-400">Willow House · Supported Living</p>
            <h4 className="text-base font-bold text-slate-900 font-display">Manager Dashboard</h4>
          </div>
          <div className="flex items-center gap-2 text-[11px] px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
            <Activity className="w-3 h-3" /> Live
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2.5">
          <Stat label="Service Users" value="42" tone="slate" trend="+3" />
          <Stat label="Shifts Today" value="18" tone="teal" trend="On track" />
          <Stat label="Open Incidents" value="2" tone="amber" trend="Review" />
          <Stat label="Readiness" value="94%" tone="emerald" trend="↑ 4%" />
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          <div className="col-span-2 rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-semibold text-slate-600">Care notes logged · 7 days</p>
              <TrendingUp className="w-3.5 h-3.5 text-teal-600" />
            </div>
            <MiniBars heights={[45, 62, 55, 80, 68, 90, 74]} />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-[11px] font-semibold text-slate-600 mb-2">Medication</p>
            <div className="relative w-16 h-16 mx-auto rounded-full" style={{ background: "conic-gradient(#0d9488 0 96%, #e2e8f0 96% 100%)" }}>
              <div className="absolute inset-2 rounded-full bg-white flex items-center justify-center text-[13px] font-bold text-slate-900">96%</div>
            </div>
            <p className="text-[10px] text-center text-slate-400 mt-1">MAR completion</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function EMARScreen() {
  const rows = [
    ["08:00", "Amlodipine 5mg", "Given", "emerald"],
    ["08:00", "Metformin 500mg", "Given", "emerald"],
    ["12:00", "Paracetamol (PRN)", "Offered", "amber"],
    ["18:00", "Atorvastatin 20mg", "Due", "slate"],
  ];
  return (
    <div className="flex min-h-[320px] text-left">
      <Sidebar active="Medication" />
      <div className="flex-1 p-4 space-y-3">
        <h4 className="text-base font-bold text-slate-900 font-display">eMAR · Margaret P.</h4>
        <div className="grid grid-cols-3 gap-2.5">
          <Stat label="Scheduled" value="6" tone="slate" />
          <Stat label="Completed" value="5" tone="emerald" />
          <Stat label="PRN" value="1" tone="amber" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {rows.map(([time, med, status, tone], i) => (
            <div key={i} className={`flex items-center gap-3 px-3 py-2.5 text-[12px] ${i > 0 ? "border-t border-slate-100" : ""}`}>
              <span className="font-mono text-slate-400 w-10">{time}</span>
              <span className="flex-1 font-medium text-slate-800">{med}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tone === "emerald" ? "bg-emerald-50 text-emerald-700" : tone === "amber" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function RotaScreen() {
  const staff = ["A. Okafor", "J. Smith", "P. Nowak", "R. Ahmed"];
  const grid = [
    [1, 1, 0, 2, 1, 0, 1],
    [0, 1, 1, 1, 0, 2, 1],
    [2, 0, 1, 1, 1, 1, 0],
    [1, 1, 1, 0, 2, 1, 1],
  ];
  const c = ["bg-slate-100", "bg-teal-500", "bg-emerald-500"];
  return (
    <div className="flex min-h-[320px] text-left">
      <Sidebar active="Rota & Visits" />
      <div className="flex-1 p-4 space-y-3">
        <h4 className="text-base font-bold text-slate-900 font-display">Rota & Visits · This week</h4>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="grid grid-cols-[80px_repeat(7,1fr)] gap-1.5 text-[10px] text-slate-400 mb-1.5 font-semibold">
            <span />
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <span key={i} className="text-center">{d}</span>)}
          </div>
          {staff.map((s, r) => (
            <div key={s} className="grid grid-cols-[80px_repeat(7,1fr)] gap-1.5 items-center mb-1.5">
              <span className="text-[11px] font-medium text-slate-700 truncate">{s}</span>
              {grid[r].map((v, i) => <span key={i} className={`h-5 rounded ${c[v]}`} />)}
            </div>
          ))}
          <div className="flex gap-3 mt-2 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-teal-500" /> Day</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500" /> Night</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CQCScreen() {
  const items = [
    ["Safe", 96], ["Effective", 92], ["Caring", 98], ["Responsive", 90], ["Well-led", 88],
  ];
  return (
    <div className="flex min-h-[320px] text-left">
      <Sidebar active="Governance" />
      <div className="flex-1 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-bold text-slate-900 font-display">CQC Readiness</h4>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold">AYOBELL indicator</span>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          {items.map(([label, val]) => (
            <div key={label}>
              <div className="flex justify-between text-[11px] mb-1">
                <span className="font-medium text-slate-700">{label}</span>
                <span className="font-bold text-slate-900">{val}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500" style={{ width: `${val}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PayrollScreen() {
  const rows = [
    ["A. Okafor", "37.5", "39.0", "£702", "Approved"],
    ["J. Smith", "30.0", "30.0", "£540", "Approved"],
    ["P. Nowak", "37.5", "41.5", "£812", "Pending"],
  ];
  return (
    <div className="flex min-h-[320px] text-left">
      <Sidebar active="Payroll" />
      <div className="flex-1 p-4 space-y-3">
        <h4 className="text-base font-bold text-slate-900 font-display">Payroll & Staff Hours</h4>
        <div className="grid grid-cols-3 gap-2.5">
          <Stat label="Payable hrs" value="1,284" tone="teal" />
          <Stat label="Projected cost" value="£23.1k" tone="slate" />
          <Stat label="Overtime" value="42h" tone="amber" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="grid grid-cols-[1.4fr_repeat(4,1fr)] px-3 py-2 text-[10px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
            <span>Staff</span><span>Sched</span><span>Worked</span><span>Gross</span><span>Status</span>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1.4fr_repeat(4,1fr)] px-3 py-2.5 text-[11px] items-center border-t border-slate-50">
              <span className="font-medium text-slate-800">{r[0]}</span>
              <span className="text-slate-500 font-mono">{r[1]}</span>
              <span className="text-slate-500 font-mono">{r[2]}</span>
              <span className="font-semibold text-slate-900">{r[3]}</span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full w-fit ${r[4] === "Approved" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{r[4]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function MigrationScreen() {
  const steps = ["Upload", "Map", "Validate", "Simulate", "Review", "Migrate"];
  return (
    <div className="flex min-h-[320px] text-left">
      <Sidebar active="Dashboard" />
      <div className="flex-1 p-4 space-y-3">
        <h4 className="text-base font-bold text-slate-900 font-display">Data Migration Centre</h4>
        <div className="flex items-center gap-1.5">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-1.5">
              <span className={`text-[10px] font-semibold px-2 py-1 rounded-md ${i < 3 ? "bg-teal-500 text-white" : i === 3 ? "bg-teal-50 text-teal-700 border border-teal-200" : "bg-slate-100 text-slate-400"}`}>{s}</span>
              {i < steps.length - 1 && <span className="w-3 h-px bg-slate-200" />}
            </div>
          ))}
        </div>
        <div className="rounded-xl border-2 border-dashed border-teal-200 bg-teal-50/40 p-5 flex flex-col items-center justify-center text-center">
          <UploadCloud className="w-7 h-7 text-teal-600 mb-1" />
          <p className="text-[12px] font-semibold text-slate-800">residents_export.csv</p>
          <p className="text-[10px] text-slate-400">2,481 rows · mapping confidence 98%</p>
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          <Stat label="Mapped" value="61/62" tone="emerald" />
          <Stat label="Warnings" value="4" tone="amber" />
          <Stat label="Critical" value="0" tone="emerald" />
        </div>
      </div>
    </div>
  );
}

export function ServiceUsersScreen() {
  const people = [
    ["MR", "Margaret Rowe", "Willow · Room 4", "Independent · Diabetes"],
    ["JT", "James Turner", "Oak House", "Mobility support"],
    ["AB", "Aisha Bello", "Willow · Room 2", "Epilepsy · PRN"],
    ["DC", "David Coyle", "Cedar House", "Dementia care"],
  ];
  return (
    <div className="flex min-h-[320px] text-left">
      <Sidebar active="Service Users" />
      <div className="flex-1 p-4 space-y-3">
        <h4 className="text-base font-bold text-slate-900 font-display">Service Users · Person Overview</h4>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {people.map((p, i) => (
            <div key={i} className={`flex items-center gap-3 px-3 py-3 ${i > 0 ? "border-t border-slate-100" : ""}`}>
              <span className="w-9 h-9 rounded-full bg-teal-50 text-teal-700 text-[11px] font-bold flex items-center justify-center">{p[0]}</span>
              <div className="flex-1">
                <p className="text-[12px] font-semibold text-slate-800">{p[1]}</p>
                <p className="text-[10px] text-slate-400">{p[2]}</p>
              </div>
              <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full">{p[3]}</span>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="text-[10px] uppercase tracking-wide text-teal-600 font-semibold">About Me</p>
          <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">"I like to keep my mornings calm. I enjoy gardening and a cup of tea at 3pm. Please involve my daughter in decisions."</p>
        </div>
      </div>
    </div>
  );
}

export function CarePlansScreen() {
  const plans = [
    ["Personal care", 100], ["Nutrition & hydration", 80], ["Mobility", 65], ["Social & wellbeing", 90],
  ];
  return (
    <div className="flex min-h-[320px] text-left">
      <Sidebar active="Care Plans" />
      <div className="flex-1 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-bold text-slate-900 font-display">Care Plan · Margaret R.</h4>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold">Person-centred</span>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          {plans.map(([label, val]) => (
            <div key={label}>
              <div className="flex justify-between text-[11px] mb-1">
                <span className="font-medium text-slate-700">{label}</span>
                <span className="font-semibold text-slate-500">{val}% reviewed</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-teal-500" style={{ width: `${val}%` }} />
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          <Stat label="Goals" value="4 active" tone="emerald" />
          <Stat label="Outcomes" value="On track" tone="teal" />
          <Stat label="Last review" value="6 Jun" tone="slate" />
        </div>
      </div>
    </div>
  );
}

export function IncidentsScreen() {
  const rows = [
    ["#1042", "Fall — no injury", "Low", "Closed", "emerald"],
    ["#1043", "Medication near-miss", "Medium", "Action plan", "amber"],
    ["#1044", "Safeguarding concern", "High", "Reported", "rose"],
  ];
  return (
    <div className="flex min-h-[320px] text-left">
      <Sidebar active="Governance" />
      <div className="flex-1 p-4 space-y-3">
        <h4 className="text-base font-bold text-slate-900 font-display">Incidents & Safeguarding</h4>
        <div className="grid grid-cols-3 gap-2.5">
          <Stat label="Open" value="3" tone="amber" />
          <Stat label="Safeguarding" value="1" tone="slate" />
          <Stat label="Actions due" value="2" tone="teal" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {rows.map((r, i) => (
            <div key={i} className={`flex items-center gap-3 px-3 py-2.5 text-[11px] ${i > 0 ? "border-t border-slate-100" : ""}`}>
              <span className="font-mono text-slate-400 w-10">{r[0]}</span>
              <span className="flex-1 font-medium text-slate-800">{r[1]}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r[4] === "emerald" ? "bg-emerald-50 text-emerald-700" : r[4] === "amber" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>{r[2]}</span>
              <span className="text-[10px] text-slate-500 w-20 text-right">{r[3]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SupervisionScreen() {
  const rows = [
    ["A. Okafor", "Supervision", "12 Jun", "Complete", "emerald"],
    ["J. Smith", "Appraisal", "20 Jun", "Scheduled", "teal"],
    ["P. Nowak", "Supervision", "02 Jun", "Overdue", "amber"],
  ];
  return (
    <div className="flex min-h-[320px] text-left">
      <Sidebar active="Governance" />
      <div className="flex-1 p-4 space-y-3">
        <h4 className="text-base font-bold text-slate-900 font-display">Supervision & Appraisals</h4>
        <div className="grid grid-cols-3 gap-2.5">
          <Stat label="Compliance" value="88%" tone="emerald" />
          <Stat label="Due this month" value="5" tone="teal" />
          <Stat label="Overdue" value="1" tone="amber" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {rows.map((r, i) => (
            <div key={i} className={`flex items-center gap-3 px-3 py-2.5 text-[11px] ${i > 0 ? "border-t border-slate-100" : ""}`}>
              <span className="flex-1 font-medium text-slate-800">{r[0]}</span>
              <span className="text-slate-500 w-20">{r[1]}</span>
              <span className="font-mono text-slate-400 w-14">{r[2]}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r[4] === "emerald" ? "bg-emerald-50 text-emerald-700" : r[4] === "teal" ? "bg-teal-50 text-teal-700" : "bg-amber-50 text-amber-700"}`}>{r[3]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const SCREEN_MAP = {
  dashboard: { label: "Manager Dashboard", icon: LayoutDashboard, Comp: ManagerDashboard },
  serviceusers: { label: "Service Users", icon: Users, Comp: ServiceUsersScreen },
  careplans: { label: "Care Plans", icon: ClipboardList, Comp: CarePlansScreen },
  emar: { label: "Medication / MAR", icon: Pill, Comp: EMARScreen },
  rota: { label: "Rota & Visits", icon: CalendarDays, Comp: RotaScreen },
  incidents: { label: "Incidents & Safeguarding", icon: AlertTriangle, Comp: IncidentsScreen },
  supervision: { label: "Supervision", icon: GraduationCap, Comp: SupervisionScreen },
  cqc: { label: "CQC Readiness", icon: ShieldCheck, Comp: CQCScreen },
  payroll: { label: "Payroll & Staff Hours", icon: Wallet, Comp: PayrollScreen },
  migration: { label: "Data Migration Centre", icon: UploadCloud, Comp: MigrationScreen },
};

/* Real AYOBELL product screenshots (sample/demo data only). When a screenKey
   has an entry here, the BrowserFrame renders the real screenshot instead of
   the CSS placeholder. Keys without an entry (emar, supervision, payroll,
   migration) intentionally remain placeholders until supplied. */
export const SCREEN_IMAGES = {
  dashboard: { src: "/screens/manager-dashboard.png", alt: "AYOBELL Manager Dashboard with CQC readiness and operational summary (sample data)" },
  serviceusers: { src: "/screens/about-me.png", alt: "AYOBELL Service User About Me profile (sample data)" },
  careplans: { src: "/screens/care-planning.png", alt: "AYOBELL Care Planning records (sample data)" },
  rota: { src: "/screens/rota-visits.png", alt: "AYOBELL Rota & Visits schedule (sample data)" },
  incidents: { src: "/screens/incident-investigation.png", alt: "AYOBELL Incident investigation record (sample data)" },
  cqc: { src: "/screens/cqc-readiness.png", alt: "AYOBELL CQC inspection readiness report (sample data)" },
};
